import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { LoadExportConfigurationsMutationResponse } from "../types/LoadExportConfigurations"

type LoadExportConfigurationsClient = typeof client<
  LoadExportConfigurationsMutationResponse,
  Error,
  never
>
type LoadExportConfigurations = {
  data: LoadExportConfigurationsMutationResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<LoadExportConfigurationsClient>>
  client: {
    parameters: Partial<Parameters<LoadExportConfigurationsClient>[0]>
    return: Awaited<ReturnType<LoadExportConfigurationsClient>>
  }
}
/**
 * @summary Load export configurations
 * @link /v1/export/load-export-configurations
 */
export function useLoadExportConfigurations(
  options: {
    mutation?: UseMutationOptions<
      LoadExportConfigurations["response"],
      LoadExportConfigurations["error"],
      LoadExportConfigurations["request"]
    >
    client?: LoadExportConfigurations["client"]["parameters"]
  } = {}
): UseMutationResult<
  LoadExportConfigurations["response"],
  LoadExportConfigurations["error"],
  LoadExportConfigurations["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    LoadExportConfigurations["response"],
    LoadExportConfigurations["error"],
    never
  >({
    mutationFn: async () => {
      const res = await client<
        LoadExportConfigurations["data"],
        LoadExportConfigurations["error"],
        LoadExportConfigurations["request"]
      >({
        method: "post",
        url: `/v1/export/load-export-configurations`,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
